import {
    getCoprincipalAccountByMailboxGuid,
    getCoprincipalAccountsByOidAndTid,
    getGlobalSettingsAccountKey,
} from 'owa-account-source-list-store';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { getQueryStringParameter } from 'owa-querystring';
import { getApplicationSettings } from 'owa-application-settings';
import { extractMailboxGuidFromRouteKey } from './resolveMailboxFromRouteKey';

/**
 * Classification of how an applink route key relates to the current OWA session.
 *
 * - `current-account`: The route key targets the currently authenticated account. Proceed normally.
 * - `different-account`: The route key targets an account that should not use explicit logon.
 * - `shared-mailbox`: The authenticating identity matches the current account, but the
 *    destination is a shared/delegate mailbox under it. Redirect via explicit logon.
 * - `unknown`: Unable to classify (missing data). Proceed with existing fallback behavior.
 */
export type ApplinkAccountMatchType =
    | 'current-account'
    | 'different-account'
    | 'shared-mailbox'
    | 'unknown';

export interface ApplinkAccountClassification {
    matchType: ApplinkAccountMatchType;
    /** The decoded `ds` query parameter (destination SMTP), if present. */
    destinationSmtp?: string;
}

/**
 * Classifies how the applink route key relates to the current OWA session account.
 * This is a classification function with no state mutation — callers decide what action to take.
 *
 * @param routeKey The raw route key from the URL (e.g., `MBX%3A<guid>%40<tenant>`)
 * @returns Classification result indicating whether the current account matches, or what type of
 * redirect is needed.
 * @throws When invoked in Monarch, where applink route-key classification is never expected.
 */
export function classifyApplinkAccountMatch(
    routeKey: string,
    queryParams?: URLSearchParams
): ApplinkAccountClassification {
    if (getApplicationSettings('PlatformType').isMonarch) {
        const error: Error = new Error(
            'Unexpected applink account classification of route key in Monarch.'
        );
        throw error;
    }

    const mailboxGuid = extractMailboxGuidFromRouteKey(routeKey);
    if (!mailboxGuid) {
        return { matchType: 'unknown' };
    }

    const currentAccountKey = getGlobalSettingsAccountKey();
    const accountForMailboxGuid = getCoprincipalAccountByMailboxGuid(mailboxGuid);

    // If the route key GUID matches the current session's primary mailbox, proceed normally
    if (
        accountForMailboxGuid &&
        getAccountKeyForMailboxInfo(accountForMailboxGuid.mailboxInfo) === currentAccountKey
    ) {
        return { matchType: 'current-account' };
    }

    // GUID doesn't match the current account. Determine if it's a different user or a shared mailbox.
    const rawDs = getQueryParamValue(queryParams, 'ds');
    const destinationSmtp = decodeSafe(rawDs);

    // Check if the `ai` (authenticating identity) resolves to the current account.
    // If it does, the link targets a shared/archive mailbox under the current user.
    const rawAi = getQueryParamValue(queryParams, 'ai');
    const ai = decodeSafe(rawAi);

    if (ai) {
        const identity = parseAiParam(ai);
        if (identity) {
            const matchingAccounts = getCoprincipalAccountsByOidAndTid(
                identity.oid,
                identity.tenantId
            );
            const aiMatchesCurrent = matchingAccounts.some(
                acct => getAccountKeyForMailboxInfo(acct.mailboxInfo) === currentAccountKey
            );

            if (aiMatchesCurrent) {
                // The authenticating identity IS the current account, but the mailbox GUID
                // didn't match the primary → this targets a shared/archive mailbox
                return { matchType: 'shared-mailbox', destinationSmtp };
            }
        }
    }

    // If the mailbox GUID matches a different coprincipal account in the store (multi-account
    // scenario, unlikely in OWA but defensive), or doesn't match anything, treat as different account
    if (accountForMailboxGuid) {
        // Different coprincipal account exists locally but isn't the global settings account
        return { matchType: 'different-account', destinationSmtp };
    }

    // GUID not found locally and ai doesn't match → different account entirely
    return { matchType: 'different-account', destinationSmtp };
}

function getQueryParamValue(
    queryParams: URLSearchParams | undefined,
    key: string
): string | undefined {
    if (queryParams) {
        return queryParams.get(key) ?? undefined;
    }

    return getQueryStringParameter(key);
}

function decodeSafe(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function parseAiParam(ai: string):
    | {
          oid: string;
          tenantId: string;
      }
    | undefined {
    const atIndex = ai.lastIndexOf('@');
    if (atIndex === -1) {
        return undefined;
    }

    const tenantId = ai.substring(atIndex + 1);
    const rawIdentity = ai.substring(0, atIndex);
    if (!tenantId || !rawIdentity) {
        return undefined;
    }

    const firstSegment = rawIdentity.split('/')[0];
    if (!firstSegment) {
        return undefined;
    }

    const prefixMatch = firstSegment.match(/^(?:OID|PUID):(.*)$/i);
    if (!prefixMatch && firstSegment.includes(':')) {
        return undefined;
    }

    const oid = (prefixMatch ? prefixMatch[1] : firstSegment).trim();
    return oid ? { oid, tenantId } : undefined;
}
